
import { Component, OnInit, ViewChild, Input, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganisationDataService, OrganisationsHierarchy } from '@services/organisation-data.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { combineLatest, firstValueFrom } from 'rxjs';
import { GlobalFunctions, imagePath, imgTypes } from '@global/GlobalFunctions';
import { LevelAsset, AssetsDataService } from '@services/assets-data.service';
import { IAccount } from '@services/accounts.service';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';
import { IQueryParameterFiltering, QueryParamType } from '@global/iFiltering';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';


/**
 * FilterQueryParams
 *
 * Defenition of Query Parameters
 */
export interface FilterQueryParams {

	watch?: boolean;

	Location?: string;

	Filter?: string;
	pageIndex?: number;
	pageSize?: number;

}


/**
 * Default Class
 */
@Component({
	selector: 'app-asset-list',
	templateUrl: './asset-list.component.html',
	styleUrls: ['./asset-list.component.scss'],
	animations: [
		trigger('detailExpand', [
			state('collapsed', style({ height: '0px', minHeight: '0' })),
			state('expanded', style({ height: '*' })),
			transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
		]),
	]
})
export class AssetListComponent implements OnInit, IQueryParameterFiltering<FilterQueryParams> {

	// =========================================================================
	// Imports / interfaces
	// =========================================================================

	// Interfaces are declared above the component.


	// =========================================================================
	// Component state
	// =========================================================================

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** Watch Status */
	public watchStatus: boolean = false

	/** watchedAssets List */
	public watchedAssets: LevelAsset[] = [];

	/** watchedAssetsFiltered List */
	public watchedAssetsFiltered: LevelAsset[] = [];

	/** flattenedAssetIDs */
	public flattenedAssetIDs: number[] = [];

	/** assetList */
	public assetList: LevelAsset[] = [];

	/** Selected Asset */
	public selectedAsset: LevelAsset

	/** accountID */
	@Input() accountID: string;

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** OrganisationID */
	@Input() public OrganisationUID: string;

	/** Expanded Element */
	public expandedElement!: LevelAsset | null;

	isMobileView: boolean = false;


	// =========================================================================
	// Query/filter configuration
	// =========================================================================

	/**
	 * Query Parameter List to use for filtering
	 *
	 * filter Query Params available to use
	 */
	public filterQueryParams: FilterQueryParams = {};

	/**
	 * filterQueryParamTypes
	 *
	 * Deffenition of the Query Parameter data types
	 */
	public filterQueryParamTypes: Record<keyof FilterQueryParams, QueryParamType> = {

		watch: 'boolean',

		Location: 'string',

		Filter: 'string',
		pageIndex: 'number',
		pageSize: 'number',

	};


	// =========================================================================
	// Table/data-source configuration
	// =========================================================================

	/** dataSourceAssets */
	public dataSourceAssets: MatTableDataSource<LevelAsset>;

	/** Table Columns to display */
	public columnsToDisplay = ['Name', 'Description']; //, 'Watch']

	/** Extended columns to display */
	columnsToDisplayWithExpand = [...this.columnsToDisplay, 'Expanded'];

	// =========================================================================
	// Paginator and sort configuration
	// =========================================================================

	private _paginatorAssets: MatPaginator;

	@ViewChild('paginatorActions')
	set paginatorAssets(paginator: MatPaginator) {
		try {
			if (!paginator) { return; }

			if (this.filterQueryParams.pageSize) { paginator.pageSize = this.filterQueryParams.pageSize }
			if (this.filterQueryParams.pageIndex) { paginator.pageIndex = this.filterQueryParams.pageIndex }

			this._paginatorAssets = paginator;
			if (this.dataSourceAssets) {
				this.dataSourceAssets.paginator = paginator;
			}
		} catch (error) {
			console.error('Error assigning Asswts paginator:', error);
		}
	}

	private _sortAssets: MatSort;

	@ViewChild('sortAssets')
	set sortAssets(sort: MatSort) {
		try {
			if (!sort) { return; }
			this._sortAssets = sort;
			if (this.dataSourceAssets) {
				this.dataSourceAssets.sort = sort;
			}
		} catch (error) {
			console.error('Error assigning Assets sort:', error);
		}
	}


	// =========================================================================
	// Location / hierarchy configuration
	// =========================================================================

	public hierarchy: OrganisationsHierarchy[];

	/** MainLocations */
	public MainLocations: OrganisationsHierarchy[] = [];

	/** locationFields */
	public locationFields: { text: string, value: string } = { text: "Name", value: "Uid" };

	/** selectedLocation */
	public selectedLocation?: OrganisationsHierarchy;

	private _locationDropdown: DropDownList;

	/** locationDropdown */
	@ViewChild('locationDropdown')
	set locationDropdown(dropdown: DropDownList) {
		try {
			if (!dropdown) { return; }

			this._locationDropdown = dropdown;
			dropdown.showClearButton = true;
		} catch (error) {
			console.error('Error assigning location Dropdown:', error);
		}
	}


	// =========================================================================
	// Syncfusion grid configuration
	// =========================================================================

	public fields: Object = { text: 'Name', value: 'AssetID' }


	// =========================================================================
	// Constructor
	// =========================================================================

	/**
	 * Default Constructor
	 * @param route route
	 * @param router router
	 * @param OrganisationData OrganisationData
	 * @param AssetsDataService AssetsDataService
	 * @param siteSubscriptions siteSubscriptions
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		private route: ActivatedRoute,
		private router: Router,
		public OrganisationData: OrganisationDataService,
		public AssetsDataService: AssetsDataService,
		private siteSubscriptions: siteSubscriptions,
		private globalFunctions: GlobalFunctions
	) {
	}


	// =========================================================================
	// Lifecycle
	// =========================================================================

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.globalFunctions.displaySpinner(true);

		if (this.route.parent) {
			combineLatest([
				this.route.paramMap,
				this.route.queryParamMap.pipe(distinctUntilChanged()),
				this.siteSubscriptions.hierarchicalData$.pipe(distinctUntilChanged())
			]).pipe(
				// Ensure that subscriptions are not null or undefined
				filter(([paramMap, queryParamMap, hierarchicalData]) =>
					!!paramMap && !!queryParamMap && !!hierarchicalData),
				takeUntilDestroyed(this.destroyRef)
			).subscribe({
				next: async ([paramMap, queryParamMap, hierarchicalData]) => {

					let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
						combineLatest([
							this.siteSubscriptions.selectedAccount$.pipe(take(1)),
							this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
							this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
							this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
						]
						)
					);

					this.accountID = selectedAccountGUID;
					this.OrganisationUID = selectedOrganisationUID;

					const currentURL = this.router.url;
					let assetID: any = currentURL.split('/').pop()
					if (assetID) {
						assetID = parseInt(assetID)
					}

					this.hierarchy = JSON.parse(hierarchicalData);
					this.MainLocations = [...(this.hierarchy[0].SubChild ?? [])];

					/** Default to look for only open cases */
					this.filterQueryParams = this.globalFunctions.queryParamsToObject<FilterQueryParams>(
						queryParamMap,
						this.filterQueryParamTypes
					);

					this.watchStatus = this.filterQueryParams.watch ?? false;

					if (this.filterQueryParams.Location) {
						this.selectedLocation = this.MainLocations.find(
							subchild => this.filterQueryParams.Location == subchild.Uid
						);
					}
					else {
						this.selectedLocation = this.hierarchy[0]
					}

					if (this.selectedLocation) {
						this.assetList = this.flattenAssets([this.selectedLocation])
						this.flattenedAssetIDs = this.assetList.map(asset => asset.AssetID);
					}

					this.selectedAsset = this.assetList.find(a => a.AssetID === assetID) ?? new LevelAsset();

					await this.getWatchList();

					this.applyQueryParameters();

					this.globalFunctions.displaySpinner(false);

				},
				error: (error) => {
					this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				}, complete: () => { this.globalFunctions.displaySpinner(false); }

			});

			this.siteSubscriptions.refreshWatchList$.pipe()
				.subscribe({
					next: (refresh) => {
						if (refresh) {
							this.getWatchList()
							this.siteSubscriptions.RefreshWatchList(false);
						}
					}
				})
		}
	}


	// =========================================================================
	// Event loading
	// =========================================================================

	/** get watched assets from the WatchListAssets table */
	async getWatchList() {

		try {
			const watchList = await firstValueFrom(
				this.AssetsDataService.getWatchListAssets$(this.accountID, this.OrganisationUID).pipe(
					filter(result => !!result)
				)
			);

			if (watchList) {
				this.watchedAssets = watchList;
			}
		}
		catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, "this.AssetsDataService.getWatchListAssets$");
			}
		} finally {
			this.assignAssetsToDataSource();
		}

	}


	// =========================================================================
	// Event table/filter configuration
	// =========================================================================

	/**
	 * assignAssetsToDataSource
	 */
	assignAssetsToDataSource() {

		if (!this.dataSourceAssets) {
			this.dataSourceAssets = new MatTableDataSource(this.watchedAssets);
		}
		else {
			this.dataSourceAssets.data = this.watchedAssets;
		}

		// this.dataSourceAssets.filterPredicate = (asset: LevelAsset, filter: string) => {

		// 	if (this.filterQueryParams.Location) {
		// 		if (!this.flattenedAssetIDs.includes(asset.AssetID)) {
		// 			return false;
		// 		}
		// 	}

		// 	const query = filter?.toLowerCase().trim() ?? '';
		// 	if (!query) { return true; }

		// 	// Combine the necessary fields for filtering
		// 	const combinedStr = [
		// 		asset.Name,
		// 		asset.Description,
		// 		asset.EquipmentCode,
		// 		asset.FunctionalLocation
		// 	]
		// 		.filter(value => value != null)
		// 		.join(' ')
		// 		.toLowerCase();

		// 	return combinedStr.includes(query)
		// };

		// this.dataSourceAssets.sortingDataAccessor = (item, property) => {
		// 	switch (property) {
		// 		case 'Description': return item.Description || '';
		// 		case 'Name': return item.Name || '';
		// 		case 'FunctionalLocation': return item.FunctionalLocation || '';
		// 		case 'EquipmentCode': return item.EquipmentCode || '';
		// 		default: return '';
		// 	}
		// };

	}

	/**
	 * onPageChange
	 */
	onPageChange(pag: MatPaginator, event: PageEvent): void {

		if (pag == this._paginatorAssets) {
			this.filterQueryParams.pageSize = pag.pageSize;
			this.filterQueryParams.pageIndex = pag.pageIndex;
		}

		this.applyQueryParameters("All", false)
	}


	// =========================================================================
	// Filtering / query parameters
	// =========================================================================

	/**
	 * Handles a change to a query parameter.
	 *
	 * @param changed - The name of the query parameter that has changed.
	 */
	onQueryParameterChange(changed: keyof typeof this.filterQueryParams, event: any) {

		if (changed == "Location") {
			if (this.locationDropdown) {
				this.locationDropdown.showClearButton = true;
			}

			if (event.itemData) {
				this.selectedLocation = <OrganisationsHierarchy>event.itemData;
				this.filterQueryParams.Location = event.value;
			}
			else if (!event.itemData) {
				this.selectedLocation = this.hierarchy[0];
				this.filterQueryParams.Location = undefined;
			}

			if (this.selectedLocation) {
				this.assetList = this.flattenAssets([this.selectedLocation]);
				this.flattenedAssetIDs = this.assetList.map(asset => asset.AssetID);
			}

		}
		else if (changed == "Filter") { }
		else if (changed == "watch") { }
		else { }

		this.applyQueryParameters(undefined, false);
	}


	/**
	 * Applies the current query parameters to the component's data.
	 *
	 * @param page - The page or data set being queried.
	 * @param reQuery - Determines whether the data should be queried again.
	 */
	applyQueryParameters(page: string = "All", reQuery: boolean = false) {

		// if (this.dataSourceAssets) {
		// 	const currentFilter = this.dataSourceAssets.filter;
		// 	this.dataSourceAssets.filter = '';
		// 	this.dataSourceAssets.filter = currentFilter || this.filterQueryParams.Filter || ' ';
		// }

		let query = this.filterQueryParams.Filter || '';
		query = query.toLowerCase().trim();

		this.watchedAssetsFiltered = this.watchedAssets.filter(asset => {

			if (this.filterQueryParams.Location) {
				if (!this.flattenedAssetIDs.includes(asset.AssetID)) {
					return false;
				}
			}

			if (query == '') { return true; }

			const combinedStr = [
				asset.Name,
				asset.Description,
				asset.EquipmentCode,
				asset.FunctionalLocation
			]
				.filter(value => value != null)
				.join(' ')
				.toLowerCase();

			return combinedStr.includes(query);
		});

		if (this.watchedAssetsFiltered.length == 0) {
			if (this.selectedAsset.AssetID > 0) {
				this.selectedAsset = new LevelAsset();
				this.router.navigate(['.'], { relativeTo: this.route, queryParams: this.filterQueryParams });
			}
		}

		this.rebuildURL(reQuery);

	}


	/**
	 * Rebuilds the URL using the current query parameter values.
	 *
	 * @param reQuery - Determines whether the data should be queried again
	 * after the URL has been rebuilt.
	 */
	rebuildURL(reQuery: boolean): void {
		if (reQuery) {
			// this.router.navigate([], { queryParams: this.filterData, queryParamsHandling: 'merge' });
			this.router.navigate([], { queryParams: this.filterQueryParams });
		} else {
			const urlTree = this.router.createUrlTree([], {
				queryParams: this.filterQueryParams,
				queryParamsHandling: 'merge'
			});

			window.history.replaceState(
				window.history.state,
				'',
				this.router.serializeUrl(urlTree)
			);
		}

	}


	// =========================================================================
	// Date handling
	// =========================================================================

	// No date handling methods currently defined.


	// =========================================================================
	// Navigation / selection
	// =========================================================================

	/**
	 * Asset Clicked Event
	 * @param asset
	 */
	assetClick(asset: any) {
		this.selectedAsset = asset;
		// console.log(asset)
	}

	/** assetSelected */
	assetSelected(asset: LevelAsset) {
		if (asset.AssetID != this.selectedAsset.AssetID) {
			this.selectedAsset = asset;
			this.showAsset(this.selectedAsset.AssetID);
		}
	}

	/**
	 *
	 * @param assetID
	 */
	private showAsset(assetID: number) {
		try {
			this.siteSubscriptions.SetSelectedAsset(this.selectedAsset)
			this.router.navigate([assetID], { relativeTo: this.route, queryParams: this.filterQueryParams });
		} catch (error) { console.error(error); }
	}


	// =========================================================================
	// Asset / hierarchy helpers
	// =========================================================================

	/**
	 * Getting list of assets from an input hierarchy
	 */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}


	// =========================================================================
	// Model helpers
	// =========================================================================

	// No model helper methods currently defined.


	// =========================================================================
	// Miscellaneous display helpers
	// =========================================================================

	/** Path to Assets Images */
	imagePathAssets(): string {
		return imagePath(this.accountID, imgTypes.organisationassets).toLowerCase()
	}

	/**
	 * Edit the Location details
	 * @param imageURL
	 */
	viewImage(imageURL: string): void {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

}
